import React, { useState } from 'react';
import './BlogPostForm.css';
import { blogService } from '../services/blogService';

const BlogPostForm = ({ onClose, onPostCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    author: '',
    tags: '',
    image: '📝',
    published: false,
    readTime: '5 min'
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const categories = [
    'Tecnología',
    'Casos de Éxito',
    'Tutorial',
    'Tendencias',
    'Guías',
    'Análisis'
  ];

  const emojis = ['📝', '🤖', '🚀', '📊', '🔗', '💡', '⚡', '🎯', '🛠️', '📈'];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'El título es obligatorio';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'El contenido es obligatorio';
    }
    
    if (!formData.excerpt.trim()) {
      newErrors.excerpt = 'El resumen es obligatorio';
    }
    
    if (!formData.category) {
      newErrors.category = 'La categoría es obligatoria';
    }
    
    if (!formData.author.trim()) {
      newErrors.author = 'El autor es obligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Procesar tags
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const postData = {
        ...formData,
        tags: tagsArray,
        content: formData.content.replace(/\n/g, '<br>') // Convertir saltos de línea a HTML
      };

      await blogService.createPost(postData);
      
      // Notificar éxito
      if (onPostCreated) {
        onPostCreated();
      }
      
      // Cerrar formulario
      onClose();
      
    } catch (error) {
      console.error('Error creating post:', error);
      setErrors({ submit: 'Error al crear el post. Intenta nuevamente.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="blog-post-form-overlay">
      <div className="blog-post-form-container">
        <div className="form-header">
          <h2>Crear Nuevo Post</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="blog-post-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Título *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Ingresa el título del post"
                className={errors.title ? 'error' : ''}
              />
              {errors.title && <span className="error-message">{errors.title}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="excerpt">Resumen *</label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                placeholder="Breve descripción del post"
                rows="3"
                className={errors.excerpt ? 'error' : ''}
              />
              {errors.excerpt && <span className="error-message">{errors.excerpt}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group flex-1">
              <label htmlFor="category">Categoría *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={errors.category ? 'error' : ''}
              >
                <option value="">Selecciona una categoría</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              {errors.category && <span className="error-message">{errors.category}</span>}
            </div>

            <div className="form-group flex-1">
              <label htmlFor="author">Autor *</label>
              <input
                type="text"
                id="author"
                name="author"
                value={formData.author}
                onChange={handleChange}
                placeholder="Nombre del autor"
                className={errors.author ? 'error' : ''}
              />
              {errors.author && <span className="error-message">{errors.author}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group flex-1">
              <label htmlFor="tags">Tags</label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="Separados por comas: IA, Tutorial, React"
              />
            </div>

            <div className="form-group">
              <label htmlFor="readTime">Tiempo de lectura</label>
              <input
                type="text"
                id="readTime"
                name="readTime"
                value={formData.readTime}
                onChange={handleChange}
                placeholder="5 min"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Imagen/Emoji</label>
              <div className="emoji-selector">
                {emojis.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    className={`emoji-option ${formData.image === emoji ? 'selected' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, image: emoji }))}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="content">Contenido *</label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="Escribe el contenido del post aquí..."
                rows="10"
                className={errors.content ? 'error' : ''}
              />
              {errors.content && <span className="error-message">{errors.content}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="published"
                  checked={formData.published}
                  onChange={handleChange}
                />
                <span className="checkbox-text">Publicar inmediatamente</span>
              </label>
            </div>
          </div>

          {errors.submit && (
            <div className="error-message submit-error">
              {errors.submit}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Guardando...' : 'Crear Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogPostForm;